/**
 * Homepage cinematic banner slider.
 * Regular + Premium slides share one stage. In-banner labels open collections.
 * Header / hash lane links still jump to that collection's first slide.
 */
class KKCampaignSlider extends HTMLElement {
  constructor() {
    super();
    this.active = 0;
    this.timer = null;
    this.transitioning = false;
    this.startX = 0;
    this.onVisibility = this.onVisibility.bind(this);
    this.onHash = this.onHash.bind(this);
    this.onLaneLink = this.onLaneLink.bind(this);
  }

  connectedCallback() {
    this.interval = Number(this.dataset.interval || 6000);
    this.duration = Number(this.dataset.duration || 1100);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.slides = Array.from(this.querySelectorAll('[data-kk-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-kk-dot]'));
    this.progress = this.querySelector('[data-kk-progress]');
    this.style.setProperty('--kk-campaign-interval', `${this.interval}ms`);

    if (!this.slides.length) return;

    this.querySelector('[data-kk-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-kk-next]')?.addEventListener('click', () => this.step(1));
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
        this.start();
      });
    });

    const stage = this.querySelector('.kk-campaign__stage');
    if (stage && this.slides.length > 1) {
      stage.addEventListener('mouseenter', () => this.stop(), { passive: true });
      stage.addEventListener('mouseleave', () => this.start());
      stage.addEventListener('focusin', () => this.stop());
      stage.addEventListener('focusout', (event) => {
        if (!stage.contains(event.relatedTarget)) this.start();
      });
      stage.addEventListener(
        'touchstart',
        (event) => {
          this.startX = event.changedTouches[0].clientX;
        },
        { passive: true }
      );
      stage.addEventListener(
        'touchend',
        (event) => {
          const delta = event.changedTouches[0].clientX - this.startX;
          if (Math.abs(delta) < 40) return;
          this.step(delta < 0 ? 1 : -1);
        },
        { passive: true }
      );
    }

    document.addEventListener('visibilitychange', this.onVisibility);
    document.addEventListener('click', this.onLaneLink);
    window.addEventListener('hashchange', this.onHash);

    if (window.Shopify && window.Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-kk-slide]');
        if (!slide) return;
        const index = this.slides.indexOf(slide);
        if (index > -1) {
          this.stop();
          this.goTo(index);
        }
      });
      this.addEventListener('shopify:block:deselect', () => this.start());
    }

    this.goTo(0, { instant: true });
    this.onHash();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('click', this.onLaneLink);
    window.removeEventListener('hashchange', this.onHash);
  }

  currentLane() {
    return this.slides[this.active]?.dataset.kkSlideLane || 'regular';
  }

  firstIndexForLane(id) {
    return Math.max(
      0,
      this.slides.findIndex((slide) => slide.dataset.kkSlideLane === id)
    );
  }

  onLaneLink(event) {
    const link = event.target.closest('[data-kk-lane-link]');
    if (!link) return;
    const id = link.dataset.kkLaneLink;
    if (!this.slides.some((slide) => slide.dataset.kkSlideLane === id)) return;
    event.preventDefault();
    this.goTo(this.firstIndexForLane(id));
    this.start();
    const close = document.querySelector('[data-kk-drawer].is-open [data-kk-drawer-close]');
    if (close) close.click();
    this.scrollIntoView({
      behavior: this.reducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  start() {
    if (this.reducedMotion || this.dataset.autoplay === 'false' || this.slides.length < 2) return;
    this.stop();
    this.restartProgress();
    this.timer = setInterval(() => this.step(1), this.interval);
  }

  stop() {
    this.classList.remove('is-playing');
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restartProgress() {
    this.classList.remove('is-playing');
    void this.offsetWidth;
    this.classList.add('is-playing');
  }

  syncLane(id) {
    this.dataset.lane = id;
    document.querySelectorAll('[data-kk-lane-link]').forEach((link) => {
      if (link.dataset.kkLaneLink === id) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    const laneSections = Array.from(document.querySelectorAll('[data-kk-lane-section]'));
    const hasTarget = laneSections.some((section) => section.dataset.kkLaneSection === id);
    if (hasTarget) {
      laneSections.forEach((section) => {
        section.hidden = section.dataset.kkLaneSection !== id;
      });
    }
    document.querySelectorAll('[data-kk-lane-end]').forEach((end) => {
      if (!hasTarget) return;
      const endsHere = end.dataset.kkLaneEnd.split(/\s+/).includes(id);
      let wrapper = end.closest('.shopify-section') || end;
      while ((wrapper = wrapper.nextElementSibling)) {
        if (!wrapper.classList.contains('shopify-section')) continue;
        wrapper.hidden = endsHere && !wrapper.querySelector(`[data-kk-lane-section="${id}"]`);
      }
    });
  }

  onHash() {
    const hash = (window.location.hash || '').toLowerCase();
    if (hash.includes('premium')) this.goTo(this.firstIndexForLane('premium'));
    else if (hash.includes('regular')) this.goTo(this.firstIndexForLane('regular'));
  }

  onVisibility() {
    if (document.hidden) this.stop();
    else this.start();
  }

  step(delta) {
    if (this.slides.length < 2) return;
    const next = (this.active + delta + this.slides.length) % this.slides.length;
    this.goTo(next);
    this.start();
  }

  start() {
    if (this.reducedMotion || this.dataset.autoplay === 'false' || this.slides.length < 2) return;
    this.stop();
    this.restartProgress();
    this.timer = setInterval(() => this.step(1), this.interval);
  }

  stop() {
    this.classList.remove('is-playing');
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restartProgress() {
    this.classList.remove('is-playing');
    void this.offsetWidth;
    this.classList.add('is-playing');
  }

  goTo(index, options = {}) {
    if (!this.slides[index]) return;
    if (!options.instant && (index === this.active || this.transitioning)) return;

    this.active = index;
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
      slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });
    this.dots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    this.syncLane(this.currentLane());

    if (options.instant) return;
    this.transitioning = true;
    setTimeout(() => {
      this.transitioning = false;
    }, this.duration);
  }
}

if (!customElements.get('kk-campaign-slider')) {
  customElements.define('kk-campaign-slider', KKCampaignSlider);
}
